import ReactDOM from 'react-dom';
import React, { useRef, useEffect } from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { Paragraph } from '../Typography';
import ChevronIconSVG from '../Icons/ChevronIcon';
import CheckMarkIconSVG from '../Icons/CheckMark';
import Button from '../Button';
import useOutsideClick from '../../utils/useOutsideClick';
import Breakpoints from '../../design-utils/breakpoints';
import { usePortal } from '../../utils/usePortal';

export enum multiSelectType {
  TITLE = 'title',
  OPTION = 'option',
}
export interface MultiSelectDropdownProps {
  buttonText: string;
  selectedOption?: string;
  helperText?: string;
  label?: string;
  options?: MultiSelectOption[];
  isOpen?: boolean;
  error?: boolean;
  withButton?: boolean;
  withPortal?: boolean;
  onOptionClicked: (option: MultiSelectOption) => void;
  onConfirm: () => void;
  onClose: () => void;
  onSelectClick: () => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedOption,
  isOpen,
  onOptionClicked,
  onConfirm,
  onSelectClick,
  onClose,
  buttonText,
  label,
  helperText,
  withButton = true,
  withPortal = true,
  error,
}) => {
  const wrapperRef = useRef(null);
  const optionRef = useRef(null);
  const portalRef = usePortal(`awt_modal_dropdown_${label}`);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement);

  const setReferenceWrapper = (ref) => setReferenceElement(ref);
  const setReferencePopperELement = (ref) => {
    setPopperElement(ref);
    wrapperRef.current = ref;
  };
  useOutsideClick(wrapperRef, onClose);

  useEffect(() => {
    if (isOpen) {
      optionRef?.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDownButton = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onSelectClick && !isOpen) {
        onSelectClick();
      }
      if (onClose && isOpen) {
        onClose();
      }
    }
  };
  const dropdownElement = (
    <DropDownContainer
      data-testid="dropdown-container"
      ref={setReferencePopperELement}
      style={styles.popper}
      {...attributes.popper}
    >
      <MultiSelectOptionsContainer>
        {options?.map((option) => {
          const { label, isSelected, type, id, customOnOptionClick } = option;
          const onOptionClick = () => {
            if (customOnOptionClick && onOptionClicked) {
              customOnOptionClick(option);
            } else if (onOptionClicked) {
              onOptionClicked(option);
            }
          };
          const handleKeyDownOption = (e: React.KeyboardEvent) => {
            if (e.key === ' ') {
              e.preventDefault();

              onOptionClick();
            }
          };

          return type === 'title' ? (
            <MultiSelectSingleOptionContainer
              key={id}
              className="bundle-multi-select-single-option-container-title"
            >
              <MultiSelectSingleOption ref={optionRef} tabIndex={0}>
                <Paragraph
                  style={{
                    marginBlock: '0px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                  modifiers={['heading', 'emphasis']}
                >
                  {label}
                </Paragraph>
              </MultiSelectSingleOption>
            </MultiSelectSingleOptionContainer>
          ) : (
            <MultiSelectSingleOptionContainer
              key={id}
              isSelected={isSelected}
              aria-checked={isSelected}
              onClick={onOptionClick}
              onKeyDown={handleKeyDownOption}
              style={{ cursor: 'pointer' }}
            >
              <MultiSelectSingleOption aria-checked={isSelected} tabIndex={0}>
                <Paragraph
                  style={{
                    marginBlock: '0px',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    maxWidth: '200px',
                  }}
                  modifiers={['body']}
                >
                  {label}
                </Paragraph>
                {isSelected && <CheckMarkIcon />}
              </MultiSelectSingleOption>
            </MultiSelectSingleOptionContainer>
          );
        })}
      </MultiSelectOptionsContainer>
      {withButton && (
        <MultiSelectSaveButtonContainer>
          <Button fullWidth filled onClick={onConfirm}>
            {buttonText}
          </Button>
        </MultiSelectSaveButtonContainer>
      )}
    </DropDownContainer>
  );
  const dropdownWrapper =
    withPortal && portalRef
      ? ReactDOM.createPortal(dropdownElement, portalRef)
      : dropdownElement;

  return (
    <>
      {isOpen && <MultiSelectBackdrop data-testid="multiselect-backdrop" />}
      <MultiSelectContainer
        tabIndex={0}
        onKeyDown={handleKeyDownButton}
        ref={setReferenceWrapper}
      >
        {isOpen ? dropdownWrapper : null}
        <MultiSelectDropdownButtonContainer
          error={!!error}
          onClick={onSelectClick}
        >
          <MultiSelectLabel>{label}</MultiSelectLabel>

          <Paragraph style={{ marginBlock: '0px' }} modifiers={['body']}>
            {selectedOption}
          </Paragraph>
          <ChevronIcon />
        </MultiSelectDropdownButtonContainer>
        {helperText && (
          <Paragraph modifiers={error ? ['body', 'destructive'] : ['body']}>
            {helperText}
          </Paragraph>
        )}
      </MultiSelectContainer>
    </>
  );
};

export default MultiSelectDropdown;

interface MultiSelectDropdownContainerProps {
  readonly open?: boolean;
}

const MultiSelectDropdownButtonContainer = styled.div.attrs({
  className: 'bundle-multi-select-dropdown-container',
})<{ error: boolean }>`
  border-radius: 8px;
  min-height: 40px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  background-color: #f8f8f8;
  cursor: pointer;
  align-items: center;
  padding: 2px 12px;
  border: ${({ error }) => (error ? '2px solid #FF002E' : '')};
`;

const MultiSelectOptionsContainer = styled.div.attrs({
  className: 'bundle-multi-select-options-container',
})<MultiSelectDropdownContainerProps>`
  background-color: #ffffff;
  min-height: 40px;
  max-height: 215px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 0px 8px #63738124;
  @media screen and (max-width: ${Breakpoints.sm}) {
    height: 100%;
  }
`;

const MultiSelectContainer = styled.div.attrs({
  className: 'bundle-multi-select-container',
})<MultiSelectDropdownContainerProps>`
  width: 100%;
  position: relative;
`;

const MultiSelectLabel = styled.div.attrs({
  className: 'bundle-multi-select-label',
})<MultiSelectDropdownContainerProps>`
  margin-block: 0px;
  position: absolute;
  top: -8px;
  background-color: #fff;
  padding: 0px 8px;
  border-radius: 8px;
  color: #637381;
  font-size: 11px;
  line-height: 13px;
`;

const MultiSelectSingleOption = styled.div.attrs({
  className: 'bundle-multi-select-single-option',
})<MultiSelectDropdownContainerProps>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  text-overflow: ellipsis;
  word-wrap: break-word;
  min-height: 24px;
  &:hover {
    background-color: #f1f2f3;
  }
`;

export interface MultiSelectSingleOptionContainerProps {
  isSelected?: boolean;
}

const MultiSelectSingleOptionContainer = styled.div.attrs<MultiSelectSingleOptionContainerProps>(
  ({ isSelected = false }) => ({
    className: `bundle-multi-select-single-option-container${
      (isSelected &&
        ' bundle-multi-select-single-option-container--selected') ||
      ''
    }`,
  }),
)<MultiSelectSingleOptionContainerProps>`
  padding: 8px 24px;
  width: 100%;
  &:hover {
    background-color: #f1f2f3;
  }
  @media screen and (max-width: ${Breakpoints.lg}) {
    padding: 16px 24px;
  }
`;

const MultiSelectSaveButtonContainer = styled.div.attrs({
  className: 'bundle-multi-select-save-button-container',
})<MultiSelectDropdownContainerProps>`
  margin-top: 16px;
  @media screen and (min-width: ${Breakpoints.lg}) {
    display: none;
  }
  @media screen and (max-width: ${Breakpoints.lg}) {
    padding: 24px;
  }
`;

const MultiSelectBackdrop = styled.div.attrs({
  className: 'multi-select-backdrop',
})`
  @media screen and (max-width: ${Breakpoints.lg}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(89, 89, 94, 0.5);
    z-index: 600;
    &:empty {
      display: block;
    }
  }
`;

const ChevronIcon = styled(ChevronIconSVG).attrs({
  className: 'bundle-multi-select-dropdown-chevron',
})`
  width: 24px;
  height: 24px;
`;
const CheckMarkIcon = styled(CheckMarkIconSVG).attrs({
  className: 'bundle-multi-select-dropdown-check-mark',
})`
  width: 24px;
  height: 24px;
`;

const DropDownContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  width: 220px;
  z-index: 90000;
  @media screen and (max-width: ${Breakpoints.lg}) {
    width: 100%;
    transform: none !important; // We need overwrite the react-popper transform
    position: fixed !important;
    inset: auto 0 0 auto !important;
    height: auto;
    max-height: 60vh;
    justify-content: flex-end;
  }
  @media screen and (max-width: ${Breakpoints.sm}) {
    bottom: 0px;
  }
  background-color: #fff;
`;
